/**
 * The desk's queue.
 *
 * The list opens on what is waiting for a person. `open` is drawn as a
 * warning rather than a neutral tone, and the open count stays visible even
 * when the filter is off: a desk that turned the filter off to look at last
 * month's refusals should still see how much work is waiting.
 *
 * The search covers the claim number, the order's and the article's, because
 * those are what somebody has in front of them when they telephone.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useDebounced, useResource } from "@hardauto/core-data";
import { day } from "@hardauto/core-i18n";
import {
  Badge,
  Button,
  Chips,
  DataTable,
  Input,
  Page,
  Pagination,
  Toolbar,
  type Chip,
  type Column,
} from "@hardauto/core-ui";

import { SupportApi, type TicketRow, type TicketStatus } from "../api";
import { OpenTicketDialog } from "../OpenTicketDialog";
import { NAMESPACE } from "../i18n";
import { SUPPORT_PATHS } from "../paths";
import { STATUSES, STATUS_TONE } from "../status";
import "./support.css";

const PAGE = 50;

export function TicketsScreen({ client }: { client: ApiClient }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const api = useMemo(() => new SupportApi(client), [client]);
  const mayWrite = useCan("support.ticket.write");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<TicketStatus | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [opening, setOpening] = useState(false);
  const term = useDebounced(search, 300);

  const fetcher = useCallback(
    () =>
      api.list({
        search: term || undefined,
        status: status ?? undefined,
        after_id: cursor,
        limit: PAGE,
      }),
    [api, term, status, cursor],
  );
  const tickets = useResource(fetcher);

  const chips: Chip[] = STATUSES.map((value) => ({ id: value, label: t(`chip.${value}`) }));

  const columns: Column<TicketRow>[] = [
    {
      key: "reference",
      header: t("column.reference"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <Link to={SUPPORT_PATHS.ticket.replace("$id", row.ticket.id)}>
            <code className="hardauto-support__reference">{row.ticket.reference}</code>
          </Link>
          <span className="hardauto-cell__muted">{t(`reason.${row.ticket.reason}`)}</span>
        </div>
      ),
    },
    {
      key: "client",
      header: t("column.client"),
      render: (row) => (
        <div className="hardauto-cell__stack">
          <span>{row.client_name}</span>
          <span className="hardauto-cell__muted">{row.client_code}</span>
        </div>
      ),
    },
    { key: "subject", header: t("column.subject"), render: (row) => row.ticket.subject },
    {
      key: "opened",
      header: t("column.opened"),
      render: (row) => day(row.ticket.opened_at, locale),
    },
    {
      key: "owner",
      header: t("column.owner"),
      secondary: true,
      // "Personne" rather than a dash: an unclaimed claim is a fact about the
      // queue, and a dash reads as missing data.
      render: (row) => row.owner_name || t("list.unassigned"),
    },
    {
      key: "status",
      header: t("column.status"),
      render: (row) => (
        <Badge tone={STATUS_TONE[row.ticket.status]}>{t(`status.${row.ticket.status}`)}</Badge>
      ),
    },
  ];

  const filtering = Boolean(term || status);

  return (
    <Page
      title={t("list.title")}
      description={t("list.description")}
      eyebrow={
        tickets.data
          ? `${t("list.count", { count: tickets.data.total })} · ${t("list.openCount", {
              count: tickets.data.open_count,
            })}`
          : undefined
      }
      actions={
        mayWrite ? (
          <Button variant="primary" onClick={() => setOpening(true)}>
            {t("open.action")}
          </Button>
        ) : undefined
      }
    >
      <Toolbar>
        <Input
          type="search"
          className="hardauto-search"
          aria-label={t("list.searchLabel")}
          placeholder={t("list.searchPlaceholder")}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setCursor(undefined);
          }}
        />
        <Chips
          chips={chips}
          active={status}
          label={t("list.filterLabel")}
          allLabel={t("list.all")}
          onSelect={(id) => {
            setStatus(id as TicketStatus | null);
            setCursor(undefined);
          }}
        />
      </Toolbar>

      <DataTable<TicketRow>
        caption={t("list.caption")}
        columns={columns}
        rows={tickets.data?.items ?? []}
        rowKey={(row) => row.ticket.id}
        loading={tickets.loading}
        loadingLabel={t("list.loading")}
        error={
          tickets.failed
            ? {
                title: t("error.title"),
                description: t("error.body"),
                onRetry: tickets.reload,
                retryLabel: t("error.retry"),
              }
            : undefined
        }
        empty={
          filtering
            ? { title: t("list.filtered.title"), description: t("list.filtered.body") }
            : { title: t("list.empty.title"), description: t("list.empty.body") }
        }
      />

      <Pagination
        nextLabel={t("list.next")}
        previousLabel={t("list.previous")}
        onNext={
          tickets.data?.next_cursor
            ? () => setCursor(tickets.data?.next_cursor ?? undefined)
            : undefined
        }
        onPrevious={cursor ? () => setCursor(undefined) : undefined}
        {...(tickets.data ? { summary: t("list.count", { count: tickets.data.total }) } : {})}
        busy={tickets.refreshing}
      />

      {mayWrite ? (
        <OpenTicketDialog
          open={opening}
          onClose={() => setOpening(false)}
          askForClient
          onOpen={(draft) => api.open({ ...draft, client_id: draft.client_id ?? "" })}
          onOpened={() => {
            setCursor(undefined);
            tickets.reload();
          }}
        />
      ) : null}
    </Page>
  );
}
